import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { Permissions, Role } from "../api";
import { errorMessage } from "../api";
import { Button, Skeleton } from "../components";
import { homeFor, useSession } from "../session";
import { Forbidden } from "./Forbidden";
import styles from "./guards.module.css";
import { ROUTES } from "./routes";

/**
 * N'ouvre l'écran qu'à une session ouverte.
 *
 * Anonyme : renvoi vers la connexion, en mémorisant l'écran demandé dans
 * l'état de navigation (jamais dans l'URL ni dans le stockage) pour y revenir
 * ensuite. Indisponible : on le dit, avec le bouton pour réessayer — envoyer
 * un utilisateur connecté sur la connexion parce que l'API a hoqueté serait le
 * pire service à lui rendre.
 */
export function RequireAuth() {
  const { state, refresh } = useSession();
  const location = useLocation();

  switch (state.status) {
    case "loading":
      return <SessionSkeleton />;
    case "unavailable":
      return (
        <div className={styles.indisponible} role="alert">
          <p className={styles.titre}>Service injoignable</p>
          <p>{errorMessage(state.error)}</p>
          <Button variant="secondary" onClick={() => void refresh()}>
            Réessayer
          </Button>
        </div>
      );
    case "anonymous":
      return (
        <Navigate
          to={ROUTES.connexion}
          replace
          state={{ from: location.pathname + location.search }}
        />
      );
    case "authenticated":
      return <Outlet />;
  }
}

/** Réservé aux anonymes : un utilisateur connecté est renvoyé chez lui. */
export function RequireAnonymous() {
  const { state } = useSession();
  if (state.status === "loading") {
    return <SessionSkeleton />;
  }
  if (state.status === "authenticated") {
    return <Navigate to={homeFor(state)} replace />;
  }
  return <Outlet />;
}

export type RequireRoleProps = {
  roles: readonly Role[];
  /**
   * Ce qu'on fait d'un rôle qui ne convient pas. `home` pour une branche de
   * layout — un commercial qui tape « / » n'a rien fait de mal, on le renvoie
   * chez lui ; `forbidden` (défaut) pour une page — un membre qui force
   * `/parametres/marque` voit pourquoi.
   */
  otherwise?: "forbidden" | "home";
};

/** N'ouvre l'écran qu'à certains rôles. À poser sous `RequireAuth`. */
export function RequireRole({ roles, otherwise = "forbidden" }: RequireRoleProps) {
  const { state } = useSession();
  if (state.status !== "authenticated") {
    // RequireAuth au-dessus a déjà tranché ; ce cas n'arrive qu'à un montage
    // mal composé, et il vaut mieux ne rien montrer que montrer trop.
    return null;
  }
  if (roles.includes(state.user.role)) {
    return <Outlet />;
  }
  if (otherwise === "home") {
    return <Navigate to={homeFor(state)} replace />;
  }
  return <Forbidden role={state.user.role} home={homeFor(state)} />;
}

export type RequirePermissionProps = {
  /** Le droit, tel que le serveur le calcule dans `Me.permissions`. */
  permission: keyof Permissions;
};

/**
 * N'ouvre l'écran qu'à une session qui porte ce droit. À poser sous
 * `RequireAuth`, pour un écran que le rôle seul ne suffit pas à ouvrir — un
 * membre relit les feuilles de temps s'il est destinataire, pas parce qu'il
 * est membre. Le serveur revérifie de toute façon ; la garde évite d'afficher
 * un écran dont chaque appel rendrait 403.
 */
export function RequirePermission({ permission }: RequirePermissionProps) {
  const { state } = useSession();
  if (state.status !== "authenticated") {
    return null;
  }
  if (state.permissions[permission]) {
    return <Outlet />;
  }
  return <Forbidden role={state.user.role} home={homeFor(state)} />;
}

/**
 * Le chemin d'accueil, résolu par la session : ESN vers ses entreprises,
 * entreprise vers son dashboard, anonyme vers la connexion. Rend une route
 * inconnue vers le bon endroit sans écran 404.
 */
export function AccueilRedirect() {
  const { state } = useSession();
  if (state.status === "loading") {
    return <SessionSkeleton />;
  }
  return <Navigate to={homeFor(state)} replace />;
}

function SessionSkeleton() {
  return (
    <div className={styles.attente} aria-busy="true" aria-label="Chargement de la session">
      <Skeleton variant="block" height="3rem" width="14rem" />
      <Skeleton lines={3} />
    </div>
  );
}
